/**
 * 可编辑输入组件
 * 支持重命名和删除功能
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Bubble, Input } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { IEditInputProps } from './types';
import './index.scss';

const EditInput: React.FC<IEditInputProps> = (props) => {
  const {
    index,
    value,
    displayValue: propsDisplayValue,
    placeholder,
    showDragIcon = false,
    showIndex = false,
    editTooltip,
    deleteTooltip,
    confirmTooltip,
    cancelTooltip,
    onRename,
    onDelete,
    className,
  } = props;
  const { commonFormatMessage } = useDblIntl('common');
  const [showInput, setShowInput] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value || '');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleEditClick = useCallback(() => {
    setShowInput(true);
  }, []);

  const handleConfirm = useCallback(() => {
    onRename?.(inputValue);
    setTimeout(() => {
      setShowInput(false);
    }, 100);
  }, [inputValue, onRename]);

  const handleCancel = useCallback(() => {
    setInputValue(value || '');
    setTimeout(() => {
      setShowInput(false);
    }, 100);
  }, [value]);

  const handleInputBlur = useCallback(() => {
    handleConfirm();
  }, [handleConfirm]);

  const handleInputEnter = useCallback(() => {
    handleConfirm();
  }, [handleConfirm]);

  const handleDelete = useCallback(() => {
    onDelete?.();
  }, [onDelete]);

  const displayValue = value || placeholder || '';

  return (
    <div className={`dbl-edit-input ${className || ''}`}>
      {showIndex && index !== undefined && (
        <span className="dbl-edit-input-index label-num">{index}</span>
      )}
      {showDragIcon && (
        <SvgIcon type="drag-icon" className="dbl-edit-input-drag-icon label-icon" />
      )}
      {!showInput ? (
        <>
          <span className="dbl-edit-input-text">{propsDisplayValue || displayValue}</span>
          {editTooltip !== false && (
            <Bubble
              dark={true}
              arrowPointAtCenter
              placement="top"
              content={
                <div>
                  <p>{editTooltip || commonFormatMessage('edit')}</p>
                </div>
              }
            >
              <i
                className="dbl-edit-input-edit-icon"
                onClick={handleEditClick}
              >
                <SvgIcon type="pencil-m" />
              </i>
            </Bubble>
          )}
          {deleteTooltip !== false && onDelete && (
            <Bubble
              dark={true}
              arrowPointAtCenter
              placement="top"
              content={
                <div>
                  <p>{deleteTooltip || commonFormatMessage('delete')}</p>
                </div>
              }
            >
              <i
                className="dbl-edit-input-delete-icon"
                onClick={handleDelete}
              >
                <SvgIcon type="clonse" />
              </i>
            </Bubble>
          )}
        </>
      ) : (
        <>
          <Input
            size="m"
            className="dbl-edit-input-input"
            value={inputValue}
            autoFocus={true}
            onChange={setInputValue}
            onPressEnter={handleInputEnter}
            onBlur={handleInputBlur}
          />
          <Bubble
            dark={true}
            arrowPointAtCenter
            placement="top"
            content={
              <div>
                <p>{confirmTooltip || commonFormatMessage('confirm')}</p>
              </div>
            }
          >
            <i
              className="dbl-edit-input-confirm-icon"
              onClick={handleConfirm}
            >
              <SvgIcon type="right-good" />
            </i>
          </Bubble>
          <Bubble
            dark={true}
            arrowPointAtCenter
            placement="top"
            content={
              <div>
                <p>{cancelTooltip || commonFormatMessage('cancel')}</p>
              </div>
            }
          >
            <i
              className="dbl-edit-input-cancel-icon dbl-edit-input-delete-icon"
              onClick={handleCancel}
            >
              <SvgIcon type="clonse" />
            </i>
          </Bubble>
        </>
      )}
    </div>
  );
};

export default EditInput;
